import { EntityType, NegotiationSide, NegotiationUpdate, ServerActionTypes } from 'api'
import { RxWebSocketReadyState } from 'messaging'
import { ConnectionActionTypes } from 'store/connection'
import { EntityStore, NegotiationRepository, RepositoryReducerActions } from './repositoryTypes'
import { RepositoryActionTypes } from './repositoryActions'
import { EntityReducer, entityStoreInitialState } from './entityReducer'

export const negotiationRepositoryInitialState: NegotiationRepository = {
  [NegotiationSide.INITIATOR]: entityStoreInitialState,
  [NegotiationSide.RESPONDER]: entityStoreInitialState
}

const entityReducer = new EntityReducer()

export function negotiationRepositoryReducer(
  state: NegotiationRepository = negotiationRepositoryInitialState,
  action: RepositoryReducerActions
): NegotiationRepository {
  switch (action.type) {
    case ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_BEGIN:
    case ServerActionTypes.ENTITY_STREAM_SNAPSHOTS_END: {
      entityReducer.resetSnapshotInternalState()
      return state
    }
    case ServerActionTypes.ENTITY_SNAPSHOT_BEGIN:
    case ServerActionTypes.ENTITY_SNAPSHOT_END:
    case ServerActionTypes.ENTITY_DELETE:
    case ServerActionTypes.ENTITY_REMOVE_SUBSCRIPTION_IDENTIFIERS: {
      const entityType = action.payload.entityType
      if (entityType !== EntityType.NEGOTIATION) {
        return state
      }

      return {
        [NegotiationSide.INITIATOR]: entityReducer.run(state[NegotiationSide.INITIATOR], action, NegotiationSide.INITIATOR) as EntityStore<
          NegotiationUpdate
        >,
        [NegotiationSide.RESPONDER]: entityReducer.run(state[NegotiationSide.RESPONDER], action, NegotiationSide.RESPONDER) as EntityStore<
          NegotiationUpdate
        >
      }
    }

    case ServerActionTypes.ENTITY_UPDATE: {
      const entityType = action.payload.update.entityType
      if (entityType !== EntityType.NEGOTIATION) {
        return state
      }
      const negotiationUpdate = action.payload.update as NegotiationUpdate

      return {
        ...state,
        [negotiationUpdate.negotiationSide]: entityReducer.run(
          state[negotiationUpdate.negotiationSide],
          action,
          negotiationUpdate.negotiationSide
        ) as EntityStore<NegotiationUpdate>
      }
    }

    case RepositoryActionTypes.REPOSITORY_UPDATE_FIELD:
    case RepositoryActionTypes.REPOSITORY_UPDATE_META:
    case RepositoryActionTypes.REPOSITORY_REVERT_UPDATES: {
      const entityType = action.payload.entityType
      if (entityType !== EntityType.NEGOTIATION) {
        return state
      }
      const ownOrder = action.meta.ownOrder ?? true
      const negotiationSide = ownOrder ? NegotiationSide.INITIATOR : NegotiationSide.RESPONDER
      return {
        ...state,
        [negotiationSide]: entityReducer.run(state[negotiationSide], action) as EntityStore<NegotiationUpdate>
      }
    }

    case ServerActionTypes.EVENT_RESPONSE: {
      const initiator = entityReducer.run(state[NegotiationSide.INITIATOR], action, NegotiationSide.INITIATOR) as EntityStore<
        NegotiationUpdate
      >
      const responder = entityReducer.run(state[NegotiationSide.RESPONDER], action, NegotiationSide.RESPONDER) as EntityStore<
        NegotiationUpdate
      >

      if (initiator === state[NegotiationSide.INITIATOR] && responder === state[NegotiationSide.RESPONDER]) {
        return state
      }

      return {
        [NegotiationSide.INITIATOR]: initiator,
        [NegotiationSide.RESPONDER]: responder
      }
    }

    case ConnectionActionTypes.SOCKET_STATUS: {
      return action.payload.readyState === RxWebSocketReadyState.Closed ? negotiationRepositoryInitialState : state
    }

    case RepositoryActionTypes.REPOSITORY_RESET: {
      return negotiationRepositoryInitialState
    }

    default: {
      return state
    }
  }
}
